// Lib
import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Selectors
import { getCurrentlyEditingId } from '../../../element/selection/currentlyEditingSelector';
import {
    getPresentationModeFocusedElementId,
    getPresentationModeFocusedElementProps,
    getPresentationModeFocusModeInitialZoomScale,
    getPresentationModeFocusModeStatus,
} from '../presentationSelector';

// Constants
import { FocusModeStatus } from '../presentationConstants';

// Actions
import { focusElementInactive, focusElementDisappearing } from '../presentationActions';
import { deselectAllElements } from '../../../element/selection/selectionActions';

// Hooks
import useUpdateEffect from '../../../utils/react/useUpdateEffect';

// Components
import FocusModeElement from './FocusModeElement';

// Styles
import './PresentationModeFocusLayer.scss';

const mapStateToProps = createStructuredSelector({
    currentlyEditingId: getCurrentlyEditingId,
    focusedElementId: getPresentationModeFocusedElementId,
    focusedElementProps: getPresentationModeFocusedElementProps,
    focusModeStatus: getPresentationModeFocusModeStatus,
    initialZoomScale: getPresentationModeFocusModeInitialZoomScale,
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchFocusElementDisappearing: () => dispatch(focusElementDisappearing()),
    dispatchFocusElementInactive: () => dispatch(focusElementInactive()),
    dispatchDeselectAll: () => dispatch(deselectAllElements()),
});

interface PresentationModeFocusLayerProps {
    children?: ReactElement;
    currentBoardId: string;
    focusedElementId: string;
    focusedElementProps: Object;
    focusModeStatus: FocusModeStatus;
    currentlyEditingId: string;
    dispatchFocusElementDisappearing: () => void;
    dispatchFocusElementInactive: () => void;
    dispatchDeselectAll: () => void;
}

const PresentationModeFocusLayer = (props: PresentationModeFocusLayerProps): ReactElement | null => {
    const {
        focusedElementId,
        focusModeStatus,
        dispatchFocusElementDisappearing,
        dispatchFocusElementInactive,
        dispatchDeselectAll,
        currentBoardId,
    } = props;
    // NOTE: Don't pass the currentlyEditingId down to the FocusModeElement otherwise Column titles
    //  won't be able to be edited. Likely due to a re-render or lifecycle function causing the edit to complete
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, focusedElementProps, currentlyEditingId, ...rest } = props;

    const isAnimatingRef = React.useRef(false);
    const animateLeaveRef = React.useRef();

    useUpdateEffect(() => {
        if (!focusedElementId) return;

        dispatchFocusElementInactive();
    }, [currentBoardId]);

    const animateFocusModeEnd = React.useCallback(async () => {
        // Don't handle if we're already animating.
        if (isAnimatingRef.current) return;
        isAnimatingRef.current = true;

        // If we're currently editing a card, simply stop editing (and deselect it) on the
        // first click of the overlay
        if (currentlyEditingId) {
            dispatchDeselectAll();
            isAnimatingRef.current = false;
            return;
        }

        dispatchDeselectAll();

        if (animateLeaveRef.current) {
            // @ts-ignore It can't recognise that we're setting this function within FocusModeElement
            await animateLeaveRef.current();
        }

        dispatchFocusElementInactive();

        isAnimatingRef.current = false;
    }, [currentlyEditingId]);

    React.useEffect(() => {
        if (focusModeStatus === FocusModeStatus.DISAPPEARING) animateFocusModeEnd();
    }, [focusModeStatus]);

    /**
     * Close the focused element.
     */
    const onLayerClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // The click occurred within the Element so don't handle it as an overlay click
        if (!e.target || (e.target as Element).closest('.Element')) return;
        if (focusModeStatus !== FocusModeStatus.ACTIVE) return;

        // If we're currently editing a card, simply stop editing (and deselect it)
        // on this click of the overlay rather than starting the disappearing transition.
        // This avoids triggering the DISAPPEARING state and animation logic when we
        // just want to exit edit mode. The focus overlay should remain active after
        // exiting edit mode - the user needs to click again to close focus mode entirely.
        if (currentlyEditingId) {
            dispatchDeselectAll();
            return;
        }

        dispatchFocusElementDisappearing();
    };

    if (!focusedElementId) return null;

    return (
        <div className="PresentationModeFocusLayer" onClick={onLayerClick}>
            <div className="presentation-mode-focus-overlay" />
            <FocusModeElement
                {...focusedElementProps}
                {...rest}
                animateLeaveRef={animateLeaveRef}
                elementId={focusedElementId}
            />
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PresentationModeFocusLayer);
